import type {
  Catalog,
  FetchCatalogListParams,
  UpdateCatalogRequestData
} from 'Services/ProductManagement/Features/Catalog/types'
import type { ListResponse } from 'Services/ProductManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  CATALOG = 'Catalog'
}

export const catalogApi = createBaseApi({
  reducerPath: 'catalogApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.CATALOG]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getCatalogs: builder.query<ListResponse<Catalog>, FetchCatalogListParams | void>({
      query: params => ({
        url: '/api/v1/catalog/',
        params: params ?? undefined
      }),
      providesTags: result => {
        if (result) {
          return [
            ...result.resources.map(({ id }) => ({
              type: TagTypes.CATALOG,
              id
            })),
            { type: TagTypes.CATALOG, id: RTKCacheTags.LIST }
          ]
        }
        return [{ type: TagTypes.CATALOG, id: RTKCacheTags.LIST }]
      }
    }),
    getSingleCatalog: builder.query<Catalog, string>({
      query: id => `/api/v1/catalog/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.CATALOG, id }]
    }),
    createSingleCatalog: builder.mutation<Catalog, Partial<Catalog>>({
      query: data => ({
        url: `/api/v1/catalog/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: TagTypes.CATALOG, id: RTKCacheTags.LIST }]
    }),
    updateSingleCatalog: builder.mutation<Catalog, { id: string } & UpdateCatalogRequestData>({
      query: data => ({
        url: `/api/v1/catalog/${data.id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: TagTypes.CATALOG, id }]
    }),
    deleteSingleCatalog: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/catalog/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: TagTypes.CATALOG, id: RTKCacheTags.LIST }]
    })
  })
})

export const {
  useGetCatalogsQuery,
  useGetSingleCatalogQuery,
  useCreateSingleCatalogMutation,
  useUpdateSingleCatalogMutation,
  useDeleteSingleCatalogMutation
} = catalogApi
